/**
 * Per-user call-method preference hook — phone-first.
 *
 * KRO is a phone-first VOIP system. The default path is always the
 * phone bridge (KRO rings the user's phone, then dials the customer).
 * Browser/WebRTC is opt-in only.
 *
 *   preferBrowser === false  →  phone bridge (default)
 *   preferBrowser === true   →  browser/WebRTC (when ready)
 *
 * Backend storage retains the legacy enum string for backwards
 * compatibility with earlier phases:
 *
 *   stored 'browser'                             → preferBrowser = true
 *   stored 'phone' | 'auto' | 'native' | unset   → preferBrowser = false
 *
 * Save path writes `'browser'` or `'phone'`.
 *
 * Module-scoped cache so every CallButton instance shares ONE fetch.
 *
 * Exposes: { preferBrowser, ready, refresh, save }
 *   save(true)  → writes 'browser'
 *   save(false) → writes 'phone'
 */
import { useEffect, useState, useCallback } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

const API = process.env.REACT_APP_BACKEND_URL || '';

const _coerce = (raw) => (String(raw || '').trim().toLowerCase() === 'browser');
const _hasChosen = (raw) => {
  const v = String(raw || '').trim().toLowerCase();
  return v === 'browser' || v === 'phone';
};

let _state = { preferBrowser: false, hasChosen: false, ready: false };
let _inflight = null;
const _subs = new Set();

const _emit = () => { _subs.forEach((fn) => { try { fn(_state); } catch (_) { /* swallow */ } }); };

const _fetchAuth = async () => {
  const t = await getAuth().currentUser?.getIdToken();
  if (!t) throw new Error('not signed in');
  return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' };
};

const _load = async () => {
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const headers = await _fetchAuth();
      const res = await fetch(`${API}/api/users/dial-preference`, { headers });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const body = await res.json();
      _state = { preferBrowser: _coerce(body?.mode), hasChosen: _hasChosen(body?.mode), ready: true };
      _emit();
      return _state;
    } catch (err) {
      // Mark ready=true even on failure so the click never blocks.
      // Default `preferBrowser=false` means phone bridge, which is
      // the intentional phone-first default.
      _state = { preferBrowser: false, hasChosen: false, ready: true };
      _emit();
      return _state;
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
};

let _authBound = false;
const _bindAuthOnce = () => {
  if (_authBound) return;
  _authBound = true;
  onAuthStateChanged(getAuth(), (user) => {
    if (user) {
      _state = { preferBrowser: false, hasChosen: false, ready: false };
      _emit();
      _load();
    } else {
      _state = { preferBrowser: false, hasChosen: false, ready: false };
      _emit();
    }
  });
};

export const savePreferBrowser = async (next) => {
  const mode = next ? 'browser' : 'phone';
  const headers = await _fetchAuth();
  const res = await fetch(`${API}/api/users/dial-preference`, {
    method: 'PUT', headers, body: JSON.stringify({ mode }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || `save failed (${res.status})`);
  }
  _state = { preferBrowser: Boolean(next), hasChosen: true, ready: true };
  _emit();
  return _state;
};

// Direct-first save: the Calling setting toggles "Ring my phone first".
//   ON  → 'phone'   (explicit bridge fallback)
//   OFF → 'native'  (direct — the default)
export const saveRingMyPhone = async (bridge) => {
  const mode = bridge ? 'phone' : 'native';
  const headers = await _fetchAuth();
  const res = await fetch(`${API}/api/users/dial-preference`, {
    method: 'PUT', headers, body: JSON.stringify({ mode }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail || `save failed (${res.status})`);
  }
  // 'phone' → bridge; 'native' → direct (not a browser opt-in, just default)
  _state = { preferBrowser: false, hasChosen: Boolean(bridge), ready: true };
  _emit();
  return _state;
};

export const refreshDialPreference = () => _load();

export default function useDialPreference() {
  const [snap, setSnap] = useState(_state);

  useEffect(() => {
    _bindAuthOnce();
    const fn = (s) => setSnap(s);
    _subs.add(fn);
    if (!_state.ready && !_inflight && getAuth().currentUser) {
      _load();
    }
    return () => { _subs.delete(fn); };
  }, []);

  const refresh = useCallback(() => _load(), []);
  const save = useCallback((next) => savePreferBrowser(next), []);
  const saveBridge = useCallback((bridge) => saveRingMyPhone(bridge), []);

  return {
    preferBrowser: snap.preferBrowser,
    hasChosen:     snap.hasChosen,
    // Direct-first view: bridge = an explicit "ring my phone first" opt-in.
    preferBridge:  snap.hasChosen && !snap.preferBrowser,
    ready:         snap.ready,
    refresh,
    save,
    saveRingMyPhone: saveBridge,
  };
}
